import { createContext, useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/cliente.js';

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  tipo: 'INTERNO' | 'CLIENTE';
  operadoraId: string | null;
  permissoes: string[];
}

interface Sessao {
  usuario: Usuario | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  /** Espelha a regra do servidor: o curinga `*` cobre tudo. */
  pode: (permissao: string) => boolean;
}

const Contexto = createContext<Sessao | null>(null);

export function ProvedorSessao({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();

  const { data: usuario, isLoading } = useQuery({
    queryKey: ['sessao'],
    queryFn: () => api.get<Usuario>('/auth/eu'),
    // 401 aqui é resposta esperada de quem não entrou ainda, não uma falha a
    // ser reprocessada — repetir só atrasaria a exibição da tela de login.
    retry: (_falhas, erro) => !(erro instanceof ApiError && erro.naoAutenticado),
    staleTime: 60_000,
  });

  const login = useMutation({
    mutationFn: (dados: { email: string; senha: string }) =>
      api.post<{ usuario: Usuario }>('/auth/login', dados),
    onSuccess: (r) => qc.setQueryData(['sessao'], r.usuario),
  });

  const logout = useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    // Limpa o cache inteiro, não só a sessão: dados de outra operadora não
    // podem sobrar em memória para o próximo login na mesma aba.
    onSuccess: () => qc.clear(),
  });

  const valor = useMemo<Sessao>(
    () => ({
      usuario: usuario ?? null,
      carregando: isLoading,
      entrar: async (email, senha) => {
        await login.mutateAsync({ email, senha });
      },
      sair: async () => {
        await logout.mutateAsync();
      },
      pode: (permissao) =>
        !!usuario && (usuario.permissoes.includes('*') || usuario.permissoes.includes(permissao)),
    }),
    [usuario, isLoading, login, logout],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao(): Sessao {
  const ctx = useContext(Contexto);
  if (!ctx) throw new Error('useSessao precisa estar dentro de ProvedorSessao');
  return ctx;
}
